/**
 * CalculateTacticalPositionAction - behavior tree action for tactical positioning.
 */

import * as THREE from 'three';

import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface CalculateTacticalPositionActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  outputPositionKey?: string;
  distance?: number;
  useNavigation?: boolean;
  angleOffset?: number;
}

@EngineClass('CalculateTacticalPositionAction')
export class CalculateTacticalPositionAction extends BehaviorAction {
  private targetActorKey: string;
  private outputPositionKey: string;
  private distance: number;
  private useNavigation: boolean;
  private angleOffset: number;

  constructor(options: CalculateTacticalPositionActionOptions = {}) {
    super({
      name: 'CalculateTacticalPosition',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.outputPositionKey = options.outputPositionKey ?? 'TacticalPosition';
    this.distance = options.distance ?? 25.0;
    this.useNavigation = options.useNavigation ?? true;
    this.angleOffset = options.angleOffset ?? 0;
  }

  protected override onInitialize(blackboard: Blackboard): void {}

  protected override onEnter(blackboard: Blackboard): void {
    this.calculateTacticalPosition(blackboard);
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    return BehaviorStatus.Success;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {}

  private calculateTacticalPosition(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    // Get target actor
    const targetActor = blackboard.getValue<Actor>(this.targetActorKey);
    if (!targetActor) {
      console.warn(`CalculateTacticalPositionAction: No target actor found with key ${this.targetActorKey}`);
      return;
    }

    // Get current positions
    const ownerPosition = new THREE.Vector3();
    const targetPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);
    targetActor.rootComponent.getWorldPosition(targetPosition);

    // Calculate direction from target to owner
    const directionToOwner = ownerPosition.clone().sub(targetPosition);
    directionToOwner.y = 0; // Keep on same height level
    directionToOwner.normalize();

    // Apply angle offset if specified
    if (this.angleOffset !== 0) {
      const rotationMatrix = new THREE.Matrix4().makeRotationY(this.angleOffset);
      directionToOwner.applyMatrix4(rotationMatrix);
    }

    // Calculate tactical position at the desired distance
    const tacticalPosition = targetPosition.clone().add(directionToOwner.multiplyScalar(this.distance));

    // If using navigation, find the closest valid point on the navigation mesh
    if (this.useNavigation) {
      const world = owner.getWorld();
      const navigationServer = world?.gameLoop?.navigationServer;

      if (navigationServer && navigationServer.isReady()) {
        const validPosition = navigationServer.getClosestPointOnNavigationMesh(tacticalPosition);
        blackboard.setValue(this.outputPositionKey, validPosition);
        return;
      }
      else {
        return;
      }
    }

    // Store the calculated position in the blackboard
    blackboard.setValue(this.outputPositionKey, tacticalPosition);
  }
}